import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle2, MapPin, Home as HomeIcon, Images, ExternalLink, Send } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from './SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RealEstateForSale = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactEmail, setContactEmail] = useState('info@shireadvisors.com');
  const [albumProperty, setAlbumProperty] = useState(null); // property whose album is open in modal
  const [inquiryProperty, setInquiryProperty] = useState(null); // property whose inquiry modal is open
  const [inquiryForm, setInquiryForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [inquirySubmitted, setInquirySubmitted] = useState(false);

  const openInquiry = (property) => {
    const isComingSoon = (property.status || 'available') === 'coming_soon';
    const prefill = isComingSoon
      ? `I would like to be notified when ${property.name} is available.\n\n`
      : `I am interested in ${property.name}.\n\n`;
    setInquiryForm({ name: '', email: '', phone: '', company: '', message: prefill });
    setInquirySubmitted(false);
    setInquiryProperty(property);
  };

  const closeInquiry = () => {
    setInquiryProperty(null);
  };

  const submitInquiry = async (e) => {
    e.preventDefault();
    if (!inquiryProperty) return;
    setSubmittingInquiry(true);
    const status = inquiryProperty.status || 'available';
    const subjectKind = status === 'coming_soon' ? 'Early interest' : 'Property inquiry';
    // Wrap the user's note with the property context so the notification
    // email immediately tells the team which listing the lead is about.
    const wrappedMessage =
      `[${subjectKind} — ${inquiryProperty.name}` +
      (inquiryProperty.location ? ` · ${inquiryProperty.location}` : '') +
      `]\n\n${inquiryForm.message.trim()}`;
    try {
      await axios.post(`${API}/contact/submit`, {
        ...inquiryForm,
        message: wrappedMessage,
      });
      setInquirySubmitted(true);
      toast.success("Inquiry sent — we'll be in touch soon.");
      setTimeout(() => {
        closeInquiry();
      }, 1800);
    } catch (err) {
      toast.error('Could not send inquiry. Please try again or call us directly.');
    } finally {
      setSubmittingInquiry(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listRes, contactRes] = await Promise.all([
          axios.get(`${API}/content/real-estate-for-sale`),
          axios.get(`${API}/content/contact`),
        ]);
        const visible = (listRes.data || [])
          .filter((p) => p.visible !== false)
          .sort((a, b) => (a.order || 0) - (b.order || 0));
        setProperties(visible);
        if (contactRes.data?.email) {
          setContactEmail(contactRes.data.email);
        }
      } catch (error) {
        console.error('Error fetching real estate listings', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <section
      className="pt-32 pb-10 bg-gradient-to-br from-gray-50 via-white to-gold-50/30"
      data-testid="real-estate-for-sale-page"
    >
      <SEO
        title="Real Estate &amp; Property Listings"
        description="Commercial real estate, hangars, and offices for sale or lease in Texas — including 4701 Spartan (KDTO), 4800 Broadway (Addison), and 516 South I-35E (Denton). Tours, financing, and disclosures available."
        path="/realestateforsale"
        jsonLd={
          properties.length > 0
            ? {
                '@context': 'https://schema.org',
                '@graph': [
                  {
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                      {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: 'https://shireadvisors.com',
                      },
                      {
                        '@type': 'ListItem',
                        position: 2,
                        name: 'Real Estate for Sale',
                        item: 'https://shireadvisors.com/realestateforsale',
                      },
                    ],
                  },
                  {
                    '@type': 'CollectionPage',
                    '@id': 'https://shireadvisors.com/realestateforsale',
                    url: 'https://shireadvisors.com/realestateforsale',
                    name: 'Real Estate & Property Listings | Shire Advisors',
                    description:
                      'Commercial properties, hangars, offices, and land available for sale or lease through Shire Advisors.',
                    inLanguage: 'en-US',
                    isPartOf: {
                      '@type': 'WebSite',
                      url: 'https://shireadvisors.com',
                      name: 'Shire Advisors',
                    },
                    mainEntity: {
                      '@type': 'ItemList',
                      numberOfItems: properties.length,
                      itemListElement: properties.map((p, idx) => {
                        const slug = (p.name || '')
                          .toLowerCase()
                          .replace(/[^a-z0-9]+/g, '-')
                          .replace(/^-+|-+$/g, '');
                        const itemUrl = `https://shireadvisors.com/realestateforsale#${slug}`;
                        const availability =
                          p.status === 'sold'
                            ? 'https://schema.org/SoldOut'
                            : p.status === 'under_contract'
                            ? 'https://schema.org/LimitedAvailability'
                            : p.status === 'coming_soon'
                            ? 'https://schema.org/PreOrder'
                            : 'https://schema.org/InStock';
                        return {
                          '@type': 'ListItem',
                          position: idx + 1,
                          url: itemUrl,
                          item: {
                            '@type': 'RealEstateListing',
                            '@id': itemUrl,
                            url: itemUrl,
                            name: p.name,
                            description: p.description || undefined,
                            image: p.photo || undefined,
                            datePosted: p.created_at || undefined,
                            ...(p.location && {
                              address: {
                                '@type': 'PostalAddress',
                                streetAddress: p.location,
                                addressCountry: 'US',
                              },
                            }),
                            offers: {
                              '@type': 'Offer',
                              url: itemUrl,
                              availability,
                              priceCurrency: 'USD',
                              ...(p.price && { price: p.price }),
                              seller: {
                                '@type': 'RealEstateAgent',
                                name: 'Shire Advisors, LLC',
                                url: 'https://shireadvisors.com',
                              },
                            },
                          },
                        };
                      }),
                    },
                  },
                ],
              }
            : null
        }
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-6">
          <span className="inline-block text-gold-600 font-semibold text-xs tracking-[0.2em] uppercase mb-2">
            Property for Sale
          </span>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif font-bold text-navy-900 mb-2 leading-tight">
            Real Estate &amp; Property Listings
          </h1>
          <p className="text-sm text-gray-600 max-w-2xl mx-auto leading-relaxed">
            The following properties are available for sale or lease as described.
          </p>
        </div>

        {/* Property grid */}
        {loading ? (
          <div className="text-center text-gray-500 py-8 text-sm">Loading…</div>
        ) : properties.length === 0 ? (
          <div className="text-center text-gray-500 py-8 text-sm">
            No properties are currently listed.
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {properties.map((property) => {
              const status = property.status || 'available';
              const isSold = status === 'sold';
              const isUnderContract = status === 'under_contract';
              const isComingSoon = status === 'coming_soon';
              // Inquiry CTA is shown for any status that's still "in play"
              const isInactive = isSold || isUnderContract;

              const statusBadge = {
                sold: {
                  classes: 'bg-red-100 text-red-700',
                  label: 'Sold',
                  icon: true,
                },
                under_contract: {
                  classes: 'bg-orange-100 text-orange-700',
                  label: 'Under Contract',
                  icon: false,
                },
                coming_soon: {
                  classes: 'bg-blue-100 text-blue-700',
                  label: 'Coming Soon',
                  icon: false,
                },
                available: {
                  classes: 'bg-gold-100 text-gold-700',
                  label: 'Available',
                  icon: false,
                },
              }[status] || { classes: 'bg-gold-100 text-gold-700', label: 'Available', icon: false };

              const slug = (property.name || '')
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');

              return (
                <article
                  key={property.id}
                  id={slug}
                  itemScope
                  itemType="https://schema.org/RealEstateListing"
                  className={`group relative overflow-hidden rounded-xl border-2 transition-all duration-300 flex flex-col h-full ${
                    isInactive
                      ? 'bg-gray-50 border-slate-200/70 opacity-75'
                      : 'panel-flair bg-white border-slate-200/70 hover:border-gold-400 hover:-translate-y-1'
                  }`}
                  data-testid={`property-item-${property.id}`}
                >
                  {/* Photo */}
                  <div className="relative h-56 bg-gray-100 overflow-hidden">
                    {property.photo ? (
                      <img
                        src={property.photo}
                        alt={`${property.name}${property.location ? ' — ' + property.location : ''}`}
                        loading="lazy"
                        decoding="async"
                        width="800"
                        height="600"
                        itemProp="image"
                        className={`w-full h-full object-cover transition-transform duration-500 ${
                          isSold ? 'grayscale' : 'group-hover:scale-105'
                        }`}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-navy-900/10 to-gold-100">
                        <HomeIcon className="w-12 h-12 text-navy-900/30" />
                      </div>
                    )}
                    {/* Status badge */}
                    <div className="absolute top-3 left-3">
                      <span
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${statusBadge.classes}`}
                      >
                        {statusBadge.icon && <CheckCircle2 className="w-3 h-3" />}
                        {statusBadge.label}
                      </span>
                    </div>
                    {/* Price pill */}
                    {property.price && (
                      <div className="absolute bottom-3 right-3">
                        <span className="inline-flex items-center px-3 py-1 bg-navy-900/90 text-white rounded-full text-sm font-bold backdrop-blur-sm">
                          {property.price}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5 flex flex-col flex-1">
                    <h3
                      itemProp="name"
                      className={`text-lg font-serif font-bold mb-1 leading-snug ${
                        isSold
                          ? 'text-gray-400 line-through'
                          : isUnderContract
                          ? 'text-gray-500'
                          : 'text-navy-900 group-hover:text-gold-600 transition-colors'
                      }`}
                    >
                      {property.name}
                    </h3>
                    {property.location && (
                      <div
                        className={`flex items-center gap-1.5 text-xs mb-2 ${
                          isInactive ? 'text-gray-400' : 'text-gray-500'
                        }`}
                      >
                        <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                        <span itemProp="address">{property.location}</span>
                      </div>
                    )}
                    {property.description && (
                      <p
                        itemProp="description"
                        className={`text-sm leading-snug mb-3 ${
                          isInactive ? 'text-gray-400' : 'text-gray-600'
                        }`}
                      >
                        {property.description}
                      </p>
                    )}
                    {!isInactive && (
                      <div className="mt-auto pt-3 space-y-2">
                        {property.album_url && (
                          <Button
                            onClick={() => setAlbumProperty(property)}
                            size="sm"
                            variant="outline"
                            className="w-full border-navy-900 text-navy-900 hover:bg-navy-900 hover:text-white"
                            data-testid={`property-album-${property.id}`}
                          >
                            <Images className="w-3.5 h-3.5 mr-1.5" />
                            View Photo Album
                          </Button>
                        )}
                        <Button
                          onClick={() => openInquiry(property)}
                          size="sm"
                          className="w-full bg-navy-900 hover:bg-navy-800 text-white"
                          data-testid={`property-inquire-${property.id}`}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1.5" />
                          {isComingSoon ? 'Express Interest' : 'Learn More'}
                        </Button>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Bottom note */}
        <div className="mt-10 text-center text-xs text-gray-500 max-w-2xl mx-auto">
          All properties are sold as-is unless otherwise noted. Contact us for
          tours, offers, disclosures, or to discuss financing.
        </div>
      </div>

      {/* Photo Album Modal */}
      <Dialog
        open={!!albumProperty}
        onOpenChange={(open) => !open && setAlbumProperty(null)}
      >
        <DialogContent
          className="max-w-5xl w-[95vw] h-[85vh] p-0 overflow-hidden flex flex-col"
          data-testid="property-album-modal"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b">
            <DialogTitle className="text-navy-900 font-serif pr-8">
              {albumProperty?.name}
              {albumProperty?.location && (
                <span className="ml-3 text-sm font-normal text-gray-500">
                  · {albumProperty.location}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 bg-gray-50 relative">
            {albumProperty?.album_url && (() => {
              const url = albumProperty.album_url;
              const isGooglePhotos =
                url.includes('photos.google.com') ||
                url.includes('photos.app.goo.gl');

              // Google blocks iframe embedding — show a clean CTA panel
              // instead of a blank iframe that looks broken.
              if (isGooglePhotos) {
                return (
                  <div className="h-full flex flex-col items-center justify-center px-8 text-center">
                    <div className="w-20 h-20 rounded-full bg-gold-100 flex items-center justify-center mb-5">
                      <Images className="w-10 h-10 text-gold-600" />
                    </div>
                    <h3 className="text-2xl font-serif font-bold text-navy-900 mb-2">
                      Photo Album Ready
                    </h3>
                    <p className="text-gray-600 max-w-md mb-6 leading-relaxed">
                      Google Photos doesn&apos;t allow albums to display inside
                      another site, so the album opens in a new tab. Your
                      Shire Advisors tab stays right here for when you&apos;re
                      done.
                    </p>
                    <Button
                      asChild
                      size="lg"
                      className="bg-navy-900 hover:bg-navy-800 text-white"
                      data-testid="property-album-open-cta"
                    >
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Open Photo Album
                      </a>
                    </Button>
                  </div>
                );
              }

              // Other services (Flickr, Imgur, self-hosted) can embed fine
              return (
                <iframe
                  title={`${albumProperty.name} photo album`}
                  src={url}
                  className="w-full h-full border-0"
                  referrerPolicy="no-referrer"
                  allow="fullscreen"
                  data-testid="property-album-iframe"
                />
              );
            })()}
          </div>
          <div className="px-6 py-3 border-t bg-white flex items-center justify-between text-sm">
            <span className="text-gray-500">
              Album viewer — you won&apos;t lose your place on the site.
            </span>
            {albumProperty?.album_url && (
              <a
                href={albumProperty.album_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-gold-600 font-semibold hover:text-gold-700 transition-colors"
                data-testid="property-album-external-link"
              >
                Open in new tab
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Inquiry / Offer Modal */}
      <Dialog
        open={!!inquiryProperty}
        onOpenChange={(open) => !open && closeInquiry()}
      >
        <DialogContent
          className="max-w-lg w-[95vw] p-0 overflow-hidden"
          data-testid="property-inquiry-modal"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b bg-navy-900 text-white">
            <DialogTitle className="text-white font-serif text-xl pr-8">
              {(inquiryProperty?.status || 'available') === 'coming_soon'
                ? 'Express Interest'
                : 'Learn More'}
            </DialogTitle>
            <DialogDescription className="text-gold-200 text-sm">
              {inquiryProperty?.name}
              {inquiryProperty?.location && (
                <span className="text-white/60"> · {inquiryProperty.location}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          {inquirySubmitted ? (
            <div className="p-8 text-center" data-testid="property-inquiry-success">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-serif font-bold text-navy-900 mb-1">
                Inquiry Sent
              </h3>
              <p className="text-sm text-gray-600">
                Thanks — we&apos;ll get back to you shortly.
              </p>
            </div>
          ) : (
            <form onSubmit={submitInquiry} className="p-6 space-y-3">
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  required
                  placeholder="Your name *"
                  value={inquiryForm.name}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, name: e.target.value })}
                  data-testid="property-inquiry-name"
                />
                <Input
                  required
                  type="email"
                  placeholder="Email *"
                  value={inquiryForm.email}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, email: e.target.value })}
                  data-testid="property-inquiry-email"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Input
                  type="tel"
                  placeholder="Phone (optional)"
                  value={inquiryForm.phone}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, phone: e.target.value })}
                  data-testid="property-inquiry-phone"
                />
                <Input
                  placeholder="Company (optional)"
                  value={inquiryForm.company}
                  onChange={(e) => setInquiryForm({ ...inquiryForm, company: e.target.value })}
                  data-testid="property-inquiry-company"
                />
              </div>
              <Textarea
                required
                rows={5}
                placeholder="Tell us a bit about your interest, timeline, or offer..."
                value={inquiryForm.message}
                onChange={(e) => setInquiryForm({ ...inquiryForm, message: e.target.value })}
                data-testid="property-inquiry-message"
              />
              <p className="text-[11px] text-gray-500 leading-relaxed">
                Submitting goes directly to {contactEmail}. All information is treated as confidential.
              </p>
              <div className="flex gap-2 pt-1">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={closeInquiry}
                  disabled={submittingInquiry}
                  data-testid="property-inquiry-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-navy-900 hover:bg-navy-800 text-white"
                  disabled={submittingInquiry}
                  data-testid="property-inquiry-submit"
                >
                  {submittingInquiry ? (
                    'Sending…'
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5 mr-1.5" />
                      Send Inquiry
                    </>
                  )}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default RealEstateForSale;
